// Importing libraries and hooks from React, Redux and others
import React, { useState, useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import L from 'leaflet';
import { useMap, useMapEvents } from 'react-leaflet/hooks';
import { isMobile, isTablet } from 'react-device-detect';
import Web3 from 'web3';
import { Alchemy, Network } from 'alchemy-sdk';

// Importing our own components and utilities
import DetectDevice from './DeviceDetector.js';
import Search from './SearchBar.js';
import MintCountdown from './MintCountdown';
import Wallet from './ConnectWallet.js';
import ToggleButton from './ToggleButton.js';
import {
  selectQueried,
  calculateCenter,
  selectSubregionLayer,
  zoomToLayer,
  fillScreen,
} from './MyMap.js';
import {
  updateAccount,
  connectWallet,
  connectCoinbase,
  connectWalletConnect,
} from './redux/blockchain/blockchainActions';
import { fetchData } from './redux/data/dataActions';
import contract from './redux/config/config.json';

// Importing icon components from various libraries
import {
  BsGrid3X3,
  BsPlayFill,
  BsPauseFill,
  BsPaletteFill,
} from 'react-icons/bs';
import { GiHamburgerMenu } from 'react-icons/gi';
import { CgSearch } from 'react-icons/cg';
import {
  AiFillCloseCircle,
  AiOutlineUnorderedList,
  AiFillStepForward,
  AiFillStepBackward,
} from 'react-icons/ai';
import { TbFocusCentered } from 'react-icons/tb';
import { BiWallet } from 'react-icons/bi';
import { FaEthereum, FaPalette } from 'react-icons/fa';

// Importing data
import ownership_full from '../data/availability_list.json';
import configuration from '../data/config.json';

// Importing styles
import '../styles/topbar.css';
import { setSelectedSubregionNull } from './MyMap.js';

// Global variables
var alch_network;
var alch_key;
var contractAddress;
var overlayTooltipText = 'overlay: grid';
var overlay_timer;
var treasure_hunt = false;

// Goerli or Ethereum Mainnet
if (contract.NETWORK.ID == 1) {
  alch_key = contract.ALCH_KEY;
  alch_network = Network.ETH_MAINNET;
  contractAddress = String(contract.CONTRACT_ADDRESS);
}
if (contract.NETWORK.ID == 5) {
  alch_key = contract.ALCH_KEY_TEST;
  alch_network = "eth-sepolia"; //Network.ETH_SEPOLIA;
  contractAddress = String(contract.CONTRACT_ADDRESS_GOERLI);
}

const alc_config = {
  apiKey: alch_key,
  network: alch_network,
};
const alchemy = new Alchemy(alc_config);

// Used to update the tooltip text for the overlay button
function sendOverlayFeedback(mode, redactNFTs) {
  var button = document.getElementsByClassName('overlay')[0];
  let tooltip = document.getElementsByClassName('tooltiptext-ovr')[0];
  if (mode === 3) {
    // no overlay
    tooltip.innerHTML = 'overlay:<br>off ';
    button.style.background = 'none';
    button.style.color = '#dcdcdc';
  } else if (mode === 0 && !redactNFTs) {
    // availabilites overlay
    tooltip.innerHTML = 'overlay:<br>availability ';
    button.style.background = '#b53b33';
    button.style.color = '#dcdcdc';
  } else if (mode === 1 || (redactNFTs && mode === 0)) {
    // class overlay
    tooltip.innerHTML = 'overlay:<br>class ';
    button.style.background = '#3340b5';
    button.style.color = '#dcdcdc';
  } else {
    // grid overlay
    tooltip.innerHTML = 'overlay:<br>grid ';
    button.style.background = 'none';
    button.style.color = '#dcdcdc';
  }
}

export const Topbar = ({
  setProjectInfo,
  sidebarState,
  setSidebar,
  mainTL,
  gridOverlay,
  setMasterTile,
  setSidebarTitle,
  projectInfo,
  setPopout,
  overlayMode,
  setOverlayMode,
  setCurrentPrompt,
  setSingleRegion,
  setSubregionMode,
  promptOverlayRef,
  setFrameNum,
  frameNum,
  isPaused,
  setIsPaused,
  animationStartTime,
  setAnimationStartTime,
  sync_flag,
  showBanner,
  setShowBanner,
  bannerMessage,
  listView,
  setListView,
  ownershipUpdated,
  setOwnershipUpdated,
  setLeadingFrameNum,
  setRedactNFTs,
  redactNFTs,
  setTilePreview,
  renderNumber,
  setRenderNumber,
  showVersionToggle,
  setlistViewExpanded,
  ownership,
  setOwnership,
  currFetchingItems,
  setCurrFetchingItems,
}) => {
  const map = useMap();
  const topbarRef = useRef(null);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [searchInput, setSearchInput] = useState('');

  const zoomToLayerClone = (map, feature, sidebarState) =>
    zoomToLayer(map, feature, sidebarState);
  const fillScreenClone = () => fillScreen();

  const selectSubregionLayerClone = (
    { promptOverlayRef },
    subregionNumObject
  ) => selectSubregionLayer({ promptOverlayRef }, subregionNumObject);

  const selectQueriedClone = ({
    map,
    gridOverlay,
    sidebarState,
    setMasterTile,
    setSidebarTitle,
    shouldZoom,
    queryNumObject,
    setTilePreview,
  }) =>
    selectQueried({
      map,
      gridOverlay,
      sidebarState,
      setMasterTile,
      setSidebarTitle,
      shouldZoom,
      queryNumObject,
      setTilePreview,
    });

  const dispatch = useDispatch();
  const blockchain = useSelector((state) => state.blockchain);

  const calculateCenterClone = () => calculateCenter();

  const getData = () => {
    if (blockchain.account !== '' && blockchain.smartContract !== null) {
      dispatch(fetchData(blockchain.account));
    }
  };

  const frameForward = () => {
    if (!isPaused) {
      setIsPaused(!isPaused);
    }
    var num_keyframes_current_render;
    if (renderNumber == 42 || renderNumber == 56) {
      num_keyframes_current_render = configuration.num_key_frames;
    }
    if (renderNumber == 14) {
      num_keyframes_current_render = configuration.num_key_frames_render14;
    }
    setFrameNum((frameNum + 1) % num_keyframes_current_render);
  };

  const frameBackward = () => {
    if (!isPaused) {
      setIsPaused(!isPaused);
    }
    var num_keyframes_current_render;
    if (renderNumber == 42 || renderNumber == 56) {
      num_keyframes_current_render = configuration.num_key_frames;
    }
    if (renderNumber == 14) {
      num_keyframes_current_render = configuration.num_key_frames_render14;
    }
    if (frameNum == 0) {
      setFrameNum(num_keyframes_current_render - 1);
    } else {
      setFrameNum((frameNum - 1) % num_keyframes_current_render);
    }
  };

  const toggleMainTLPlay = () => {
    if (!isPaused) {
      if (mainTL.current && mainTL.current.options.opacity == 1) {
        var num_keyframes_current_render;
        var duration;
        if (renderNumber == 42 || renderNumber == 56) {
          num_keyframes_current_render = configuration.num_key_frames;
          duration = 10000;
        }
        if (renderNumber == 14) {
          num_keyframes_current_render = configuration.num_key_frames_render14;
          duration = 6400;
        }
        let msElapsed = (Date.now() - animationStartTime) % duration;
        setFrameNum(
          Math.round((num_keyframes_current_render * msElapsed) / duration) %
            num_keyframes_current_render
        );
      }
      sync_flag.current = 0;
      let loading = document.getElementsByClassName('loading')[0];
      if (loading) {
        loading.style.display = 'none';
      }
    } else {
      setLeadingFrameNum(-1);
      setAnimationStartTime(Date.now());
    }
    setIsPaused(!isPaused);
  };

  //error case for switching between renders
  useEffect(() => {
    if (frameNum > 7 && renderNumber == 14) {
      setLeadingFrameNum(-1);
      setFrameNum(7);
    }
  }, [renderNumber]);

  //Multiplier for calc TPX
  function sumOwnershipValues(ownership) {
      let sum = 0;
      for (const key in ownership) {
          if (parseInt(key) >= 73359200) {
              sum += parseInt(key) * 4;
          } else if (parseInt(key) >= 8954400) {
              sum += parseInt(key) * 3;
          } else if (parseInt(key) >= 1496400) {
              sum += parseInt(key) * 2;
          } else {
              sum += parseInt(key);
          }
      }
      return sum;
  }

  //Function to get user owned NFTs and update ownership values for those tokenId to 2
  async function fetchCurrUserNFTs(addr, updatedOwnership) {
    let web3;
    web3 = new Web3(window.ethereum);
    let nfts;
    const currWallet = String(addr); 
    var requestOptions = {
      method: 'GET',
    };

    if (currWallet !== '' && currWallet !== 'null') {
      nfts = await alchemy.nft.getNftsForOwner(currWallet, {
              contractAddresses: [contractAddress],
      });
    }
    if (nfts) {
      for (let nft of nfts.ownedNfts) {
        if (
          nft.contract.address.toLowerCase() === contractAddress.toLowerCase()
        ) {
          updatedOwnership[nft.tokenId] = 2;
        }
      }
    }
    return updatedOwnership;
  }

  //Function to check all minted NFTS, set ownership to 1.
  const checkOwner = async () => {
    const address = String(contractAddress);
    const updatedOwnership = {...ownership}; 
    const opts = {
      pageKey: '1000',
      omitMetadata: false,
      pageSize: 10,
      tokenUriTimeoutInMs: 0,
    };
    let i = 1;
    for await (const nft of alchemy.nft.getNftsForContractIterator(
      address,
      opts
    )) {
      updatedOwnership[nft.tokenId] = 1;
      //uncomment and run once to update metadata
      /*
          if(nft.tokenId>32856800){
          let currTokenId = nft.tokenId;
          const options = {
            method: 'GET',
            headers: {
              accept: 'application/json',
              'X-API-KEY': 'e10ecd557f4a4b0da85551f07da32343',
            },
          };
          const url = "https://api.opensea.io/api/v2/chain/ethereum/contract/0xe4ebab52aad527c5ee51f218bf10109f4145524c/nfts/" + currTokenId + "/refresh";
          console.log("Done with " + currTokenId);                
          await fetch(url, options);
        }*/
      i++;
    }

    return updatedOwnership;
  };

  //When user account is updated, first check minted NFTS, then user owned, then set ownership.
  useEffect(() => {
    async function updateOwnership() {
      setCurrFetchingItems(true);
      checkOwner()
        .then(updatedOwnership => {
          return fetchCurrUserNFTs(blockchain.account, updatedOwnership);
        })
        .then(newOwnership => {
          setOwnership(newOwnership);
        })
        .catch(error => {
          console.error("Error updating ownership:", error);
        });

      //let tempOwnership = await checkOwner();
      //let newOwnership = await fetchCurrUserNFTs(blockchain.account, tempOwnership);
      //setOwnership(newOwnership);
      if(!ownershipUpdated){
        setOwnershipUpdated(true);
      }
    }

    updateOwnership(); // Call the function
  }, [blockchain.account]);

  //On disconnect wallet, update blockchain.account, reset ownership
  async function dicconnectWalletPressed() {
    dispatch(updateAccount(''));
    const updatedOwnership = {...ownership};
    Object.keys(updatedOwnership).forEach((key) => {
      {
        if (updatedOwnership[key] == 2) {
          updatedOwnership[key] = 1;
        }
      }
    })
    setOwnership(updatedOwnership);
    //map.flyTo(calculateCenterClone(), fillScreenClone(), { animate: 1, easeLinearity: 0 });
    localStorage.setItem('isMetamaskWalletConnected', false);
    localStorage.setItem('isWalletConnectConnected', false);
    localStorage.setItem('isCoinbaseWalletConnected', false);
  }
  //This is really just connect metamask. Remembers prev metamask connection. Sets localstorage var to save.
  async function connectWalletPressed() {
    let web3;
    try {
      await dispatch(connectWallet());
      getData();
      web3 = new Web3(window.ethereum);
      web3.eth.getAccounts().then(async (addr) => {
        console.log(addr)
        if (addr.length > 0) {
          localStorage.setItem('isMetamaskWalletConnected', true);
          localStorage.setItem('isWalletConnectConnected', false);
          localStorage.setItem('isCoinbaseWalletConnected', false);
        } else {
          dispatch(updateAccount(''));
          localStorage.setItem('isMetamaskWalletConnected', false);
          localStorage.setItem('isWalletConnectConnected', false);
          localStorage.setItem('isCoinbaseWalletConnected', false);
        }
      });
    } catch (e) {
      console.log(e)
      localStorage.setItem('isMetamaskWalletConnected', false);
    }
  }
  //Connect coinbase, set vars to remember
  async function connectCoinbasePressed() {
    let web3;
    try {
      await dispatch(connectCoinbase());
      getData();
      web3 = new Web3(window.ethereum);
      web3.eth.getAccounts().then(async (addr) => {
        if (addr.length > 0) {
          localStorage.setItem('isCoinbaseWalletConnected', true);
          localStorage.setItem('isWalletConnectConnected', false);
          localStorage.setItem('isMetamaskWalletConnected', false);
        } else {
          dispatch(updateAccount(''));
          localStorage.setItem('isCoinbaseWalletConnected', false);
          localStorage.setItem('isWalletConnectConnected', false);
          localStorage.setItem('isMetamaskWalletConnected', false);
        }
      });
    } catch (e) {
      localStorage.setItem('isCoinbaseWalletConnected', false);
    }
  }
  //Connect walletconnect, set vars to remember
  async function connectWalletConnectPressed() {
    let web3;
    try {
      await dispatch(connectWalletConnect());
      getData();
      localStorage.setItem('isWalletConnectConnected', true);
      localStorage.setItem('isCoinbaseWalletConnected', false);
      localStorage.setItem('isMetamaskWalletConnected', false);
    } catch (e) {
      dispatch(updateAccount(''));
      localStorage.setItem('isWalletConnectConnected', false);
    }
    console.log(localStorage?.getItem('isWalletConnectConnected'));
  }

  function sync() {
    if (mainTL.current) {
      mainTL.current.remove();
      mainTL.current.addTo(map);
    }
  }
  //hamburger menu for handling mobile
  function hamburger() {
    // TO-DO: CLEAN UP THE CODE, CAN PUT ALL UNDER THE BTTN-CONTAINER
    var background = document.getElementsByClassName('hamburger-background')[0];
    var overlay = document.getElementsByClassName('overlay')[0];
    var wallet = document.getElementsByClassName('title')[0];
    var search = document.getElementsByClassName('search')[0];
    const recenter = document.getElementsByClassName('recenter-mobile')[0];
    let tooltiptext = document.getElementsByClassName('tooltiptext')[0];
    const bar = document.getElementsByClassName('search-container')[0];
    const input = document.getElementsByClassName('search-input')[0];
    const close = document.getElementsByClassName('search-close')[0];
    var video = document.getElementsByClassName('grid-item-2')[0];
    var sidebar = document.getElementsByClassName('sidebar')[0];
    var button_container = document.getElementsByClassName(
      'topbar-bttn-container'
    )[0];
    var wmobile = document.getElementsByClassName('wallet-mobile')[0];

    var playbox = document.getElementsByClassName('bttn frame-controller')[0];
    var loading = document.getElementsByClassName('loading')[0];

    var vid = document.getElementsByClassName('video1')[0];
    var toggleRenderButton =
      document.getElementsByClassName('toggleRenderButton')[0];

    if (
      background.style.visibility === 'hidden' ||
      background.style.visibility === ''
    ) {
      button_container.style = 'pointer-events: all';

      background.style.visibility = 'visible';
      overlay.style.visibility = 'visible';
      if (wallet) {
        wallet.style.visibility = 'visible';
      }
      search.style.visibility = 'visible';
      recenter.style.visibility = 'visible';
      map.smoothWheelZoom.enable();
      map.dragging.enable();

      input.style.visibility = 'visible';
      bar.style.visibility = 'visible';
      close.style.visibility = 'visible';

      // playbox.style.visibility = "hidden"
      loading.style.visibility = 'hidden';
      loading.style.display = 'none';
      toggleRenderButton.style.visibility = 'hidden';

      if (video !== undefined && video.style.visibility === 'visible') {
        video.style.height = 'calc(100dvh - 350.5px)';
        vid.style.maxHeight = 'calc(100dvh - 392px)';
      }
      setProjectInfo(0);
    } else {
      button_container.style = 'pointer-events: none';
      let searchInput = document.getElementsByClassName('search-input')[0];
      background.style.visibility = 'hidden';
      if (searchInput.style.visibility === 'visible') {
        closeSearch();
      }
      if (wallet) {
        wallet.style.visibility = 'hidden';
      }
      // wmobile.style.visibility = 'hidden';
      //toggleRenderButton.style.visibility  = 'visible'
      overlay.style.visibility = 'hidden';
      search.style.visibility = 'hidden';
      input.style.visibility = 'hidden';
      bar.style.visibility = 'hidden';
      close.style.visibility = 'hidden';
      recenter.style.visibility = 'hidden';
      toggleRenderButton.style.visibility = 'visible';

      if (video !== undefined && video.style.visibility === 'visible') {
        video.style.height = 'calc(100dvh - 305.5px)';
        vid.style.maxHeight = 'calc(100dvh - 335px)';
      }
      // playbox.style.visibility = "visible"
    }
  }

  function playClose() {
    var toggleRenderButton =
      document.getElementsByClassName('toggleRenderButton')[0];

    // console.log(projectInfo)
    let playbox = document.getElementsByClassName('bttn frame-controller')[0];
    let loading = document.getElementsByClassName('loading')[0];
    if (projectInfo == 1) {
      if (playbox) {
        playbox.style.visibility = 'visible';
        toggleRenderButton.style.visibility = 'visible';
      }
    } else {
      if (playbox) {
        playbox.style.visibility = 'hidden';
        toggleRenderButton.style.visibility = 'hidden';
      }
      if (loading) {
        loading.style.display = 'none';
        loading.style.visibility = 'hidden';
      }
    }
  }

  function openSearch() {
    const bar = document.getElementsByClassName('search-container')[0];
    const input = document.getElementsByClassName('search-input')[0];
    const close = document.getElementsByClassName('search-close')[0];
    const matches = document.getElementsByClassName('matches-container')[0];
    const hamburger = document.getElementsByClassName(
      'hamburger-background'
    )[0];
    const wallet = document.getElementsByClassName('title')[0];
    const overlay = document.getElementsByClassName('overlay')[0];
    const recenter = document.getElementsByClassName('recenter-mobile')[0];

    // bar.style.width = 30 + "dvw";
    // input.style.width = 30 + "dvw";
    input.style.visibility = 'visible';
    bar.style.visibility = 'visible';
    close.style.visibility = 'visible';
    matches.style.visibility = 'hidden';
    matches.style.display = 'none';
    //input.style.transition = "all 1s";
    bar.style.animation = 'fadeIn 0.75s';
    if (hamburger.style.visibility === 'visible') {
      // bar.style.width = 91 + "dvw";
      // input.style.width = 85 + "dvw";
      overlay.style.display = 'none';
      recenter.style.display = 'none';
    }
  }

  function closeSearch() {
    const bar = document.getElementsByClassName('search-container')[0];
    const input = document.getElementsByClassName('search-input')[0];
    const close = document.getElementsByClassName('search-close')[0];
    const matches = document.getElementsByClassName('matches-container')[0];
    const hamburger = document.getElementsByClassName(
      'hamburger-background'
    )[0];
    const wallet = document.getElementsByClassName('title')[0];
    const overlay = document.getElementsByClassName('overlay')[0];
    const recenter = document.getElementsByClassName('recenter-mobile')[0];

    setSearchInput('');

    matches.style.visibility = 'hidden';
    matches.style.display = 'none';

    if (hamburger.style.visibility === 'visible') {
      bar.style.animation = 'fadeSearch 0.5s';
      input.style.animation = 'fadeSearch 0.5s';
      close.style.animation = 'fadeSearch 0.5s';
      //bar.style.width = 0 + "px";
      //input.style.width = 0 + "px";
      wallet.style.animation = 'slideOut linear 0.25s';
      wallet.style.visibility = 'visible';
      overlay.style.display = 'flex';
      recenter.style.display = 'flex';
    } else {
      bar.style.animation = 'fadeIn 0s';
      bar.style.animation = 'fadeSearch 0s';
      input.style.animation = 'fadeSearch 0s';
      close.style.animation = 'fadeSearch 0s';
    }

    bar.style.visibility = 'hidden';
    input.style.visibility = 'hidden';
    close.style.visibility = 'hidden';
  }

  //If user had connected a wallet prev check if connection is still valid
  // Users can disconnect on wallet side, this is detected
  useEffect(() => {
    const checkConnection = async () => {
      let web3;
      if (localStorage?.getItem('isMetamaskWalletConnected') === 'true') {
        try {
          if (window.ethereum) {
            web3 = new Web3(window.ethereum);
            web3.eth.getAccounts().then(async (addr) => {
              if (addr.length > 0) {
                dispatch(connectWallet());
                getData();
                localStorage.setItem('isWalletConnectConnected', false);
                localStorage.setItem('isCoinbaseWalletConnected', false);
              } else {
                dispatch(updateAccount(''));
                const updatedOwnership = {...ownership};
                Object.keys(updatedOwnership).forEach((key) => (updatedOwnership[key] = 0));
                setOwnership(updatedOwnership);
                localStorage.setItem('isMetamaskWalletConnected', false);
              }
            });
          }
        } catch (e) {
          dispatch(updateAccount(''));
          localStorage.setItem('isMetamaskWalletConnected', false);
        }
      }
      if (localStorage?.getItem('isCoinbaseWalletConnected') === 'true') {
        try {
          if (window.ethereum) {
            web3 = new Web3(window.ethereum);
            web3.eth.getAccounts().then(async (addr) => {
              if (addr.length > 0) {
                dispatch(connectCoinbase());
                getData();
                localStorage.setItem('isMetamaskWalletConnected', false);
                localStorage.setItem('isWalletConnectConnected', false);
              } else {
                dispatch(updateAccount(''));
                const updatedOwnership = {...ownership};                
                Object.keys(updatedOwnership).forEach((key) => (updatedOwnership[key] = 0));
                setOwnership(updatedOwnership);
                localStorage.setItem('isCoinbaseWalletConnected', false);
              }
            });
          }
        } catch (e) {
          dispatch(updateAccount(''));
          localStorage.setItem('isCoinbaseWalletConnected', false);
        }
      }

      if (localStorage?.getItem('isWalletConnectConnected') === 'true') {
        try {
          await dispatch(connectWalletConnect());
          getData();
          localStorage.setItem('isMetamaskWalletConnected', false);
          localStorage.setItem('isCoinbaseWalletConnected', false);
        } catch (e) {
          dispatch(updateAccount(''));
          const updatedOwnership = {...ownership};  
          Object.keys(updatedOwnership).forEach((key) => (updatedOwnership[key] = 0));
          setOwnership(updatedOwnership);
          localStorage.setItem('isWalletConnectConnected', false);
        }
      }
    };
    checkConnection();
  }, []);

  useEffect(() => {
    L.DomEvent.disableClickPropagation(topbarRef.current);
  });
  return (
    <div
      className="topbar unselectable"
      ref={topbarRef}
      style={{ backgroundColor: configuration.frame_background_color }}
    >
      {!isMobile && (
        <div
          className="experience"
          style={{
            display: 'flex',
            position: 'absolute',
            right: 18,
            fontSize: 20,
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={() => {
            setRedactNFTs(!redactNFTs);
          }}
        >
          {/* {redactNFTs ? ( */}
          {/* <FaEthereum /> */}
          {/* ) : ( */}
          <svg
            style={{ opacity: redactNFTs ? 0.5 : 1 }}
            stroke="currentColor"
            // opacity={redactNFTs ? 0.5 : 1}
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 320 512"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M311.9 260.8L160 353.6 8 260.8 160 0l151.9 260.8z"></path>
            <path
              d="M160 383.4L8 290.6 160 512l152-221.4-152 92.8z"
              style={{ color: '#959595' }}
            ></path>
          </svg>
          {/* )} */}
          <div
            className="tooltiptext tooltiptext-experience"
            style={{
              backgroundColor: configuration.tooltip_background_color,
              color: configuration.tooltip_text_color,
            }}
          >
            {redactNFTs ? 'art mode' : 'web3 mode'}
          </div>
        </div>
      )}

      <MintCountdown
        showBanner={showBanner}
        setShowBanner={setShowBanner}
        bannerMessage={bannerMessage}
        redactNFTs={redactNFTs}
      />

      <div id="feedback-container">tile url copied</div>

      <div className="hamburger-container">
        <div
          className="hamburger-menu"
          onClick={() => {
            hamburger();
          }}
        >
          <GiHamburgerMenu className="icon" style={{ fontSize: 28 }} />
        </div>

        <div className="hamburger-background"> </div>
        {DetectDevice ? (
          <div className="bttn frame-controller">
            <AiFillStepBackward
              onClick={() => {
                frameBackward();
              }}
              className="icon"
              style={{ fontSize: 35, pointerEvents: 'all', marginRight: 10 }}
            />

            {isPaused ? (
              <BsPlayFill
                className="icon"
                onClick={() => {
                  toggleMainTLPlay();
                }}
                style={{ fontSize: 45, pointerEvents: 'all' }}
              />
            ) : (
              <BsPauseFill
                className="icon"
                onClick={() => {
                  toggleMainTLPlay();
                }}
                style={{ fontSize: 45, pointerEvents: 'all' }}
              />
            )}

            <AiFillStepForward
              onClick={() => {
                frameForward();
              }}
              className="icon"
              style={{ fontSize: 35, pointerEvents: 'all', marginLeft: 10 }}
            />
          </div>
        ) : null}
        {/* {DetectDevice ? ( */}
        <div className="bttn loading" style={{ backgroundColor: '#bl201634' }}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="white"
          >
            <rect
              className="spinner_9y7u"
              x="1"
              y="1"
              rx="1"
              width="10"
              height="10"
            />
            <rect
              className="spinner_9y7u spinner_DF2s"
              x="1"
              y="1"
              rx="1"
              width="10"
              height="10"
            />
            <rect
              className="spinner_9y7u spinner_q27e"
              x="1"
              y="1"
              rx="1"
              width="10"
              height="10"
            />
          </svg>
        </div>
        {/* ) : null} */}
      </div>

      <div
        className="info"
        onClick={() => {
          if (projectInfo & !DetectDevice) {
            setProjectInfo(0);
            map.smoothWheelZoom.enable();
            map.dragging.enable();
          }

          let background = document.getElementsByClassName(
            'hamburger-background'
          )[0];

          if (background.style.visibility === 'visible') {
            hamburger();
          }

          console.log(projectInfo);
          setProjectInfo(!projectInfo);
          playClose();
        }}
      >
        <span
          className="tooltiptext tooltiptext-info "
          style={{
            backgroundColor: configuration.tooltip_background_color,
            color: configuration.tooltip_text_color,
          }}
        >
          info
        </span>
        <p style={{ fontSize: 25, color: 'white' }}>i</p>
      </div>

      <div className="topbar-bttn-container">
        {!DetectDevice && !redactNFTs ? (
          <div
            className="bttn listview"
            onClick={() => {
              setListView(!listView);
            }}
          >
            <span
              className="tooltiptext tooltiptext-listview"
              style={{
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              listview
            </span>
            <AiOutlineUnorderedList
              className="icon"
              style={{
                fontSize: 25,
              }}
            />
          </div>
        ) : null}

        {DetectDevice ? (
          <div
            className="bttn overlay"
            // style={{ backgroundColor: toggle ? 'black' : 'white', color: toggle ? 'white' : 'black' }}
            onClick={() => {
              if (overlayMode === 3) {
                if (redactNFTs) {
                  setOverlayMode(1);
                  sendOverlayFeedback(1, redactNFTs);
                } else {
                  setOverlayMode(0);
                  sendOverlayFeedback(0, redactNFTs);
                }
              } else {
                setOverlayMode(overlayMode + 1);
                sendOverlayFeedback(overlayMode + 1, redactNFTs);
              }
            }}
          >
            <span
              className="tooltiptext tooltiptext-ovr"
              style={{
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              {overlayTooltipText}
            </span>
            <BsGrid3X3
              className="icon"
              style={{
                fontSize: 20,
              }}
            />
          </div>
        ) : (
          <div
            className="bttn overlay"
            style={{ width: 28, padding: 3 }}
            // style={{ backgroundColor: toggle ? 'black' : 'white', color: toggle ? 'white' : 'black' }}
            onClick={() => {
              if (overlayMode === 3) {
                if (redactNFTs) {
                  setOverlayMode(1);
                  sendOverlayFeedback(1, redactNFTs);
                } else {
                  setOverlayMode(0);
                  sendOverlayFeedback(0, redactNFTs);
                }
              } else {
                setOverlayMode(overlayMode + 1);
                sendOverlayFeedback(overlayMode + 1, redactNFTs);
              }
              var overlayText =
                document.getElementsByClassName('tooltiptext-ovr')[0];
              overlayText.style.display = 'block';
              // clearTimeout(overlay_timer);
              // overlay_timer = setTimeout(() => {
              //   overlayText.style.display = 'none';
              // }, 3000);
            }}
          >
            <span
              className="tooltiptext tooltiptext-ovr"
              style={{
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              {overlayTooltipText}
            </span>
            <BsGrid3X3 className="icon" style={{ fontSize: 28 }} />
          </div>
        )}

        {!DetectDevice ? null : (
          <div
            className="bttn recenter-mobile"
            onClick={() => {
              setSidebar(0);
              map.flyTo(calculateCenterClone(), fillScreenClone(), {
                animate: 1,
                easeLinearity: 0,
              });
              var playbox = document.getElementsByClassName(
                'bttn frame-controller'
              )[0];
              playbox.style.visibility = 'visible';
              playbox.style.transform =
                'translate(calc(50vw - 50px), calc(100dvh - 45px))';

              var urlId = '/todem/';
              window.history.pushState('', '', urlId);
            }}
          >
            <TbFocusCentered className="icon" style={{ fontSize: 28 }} />
          </div>
        )}

        {/* {!DetectDevice ? null : (
          <div
            className="bttn wallet-mobile"
            onClick={() => {
              connectWalletPressed();
            }}
          >
            <BiWallet className="icon" style={{ fontSize: 25 }} />
          </div>
        )} */}
        {/* <img
                      src="images/metamask.svg"
                      alt="Metamask Icon"
                      id="metamask"
                      style={{ opacity: 0.2, filter: 'grayscale(100%)', pointerEvents: "none" }}
                      onClick={() => {
                        //window.open(setLink(currentTile.tokenId), '_blank');
                      }}>
                    
                      </img>
    } */}

        {!DetectDevice ? (
          <div className="bttn search">
            <CgSearch
              className="icon"
              style={{ opacity: treasure_hunt ? '0.3' : '1.0', fontSize: '19' }}
            />
          </div>
        ) : (
          <div className="bttn search">
            <CgSearch
              className="icon"
              style={{ fontSize: 22, opacity: treasure_hunt ? 0.3 : 1.0 }}
            />
            {treasure_hunt ? (
              <div
                style={{
                  left: 35,
                  textAlign: 'left',
                  position: 'absolute',
                  height: '100%',
                  width: '100dvh',
                  display: 'flex',
                  fontFamily: 'Terminus',
                  fontSize: 14.5,
                  opacity: 0.8,
                  alignItems: 'center',
                }}
              >
                Disabled during Treasure Hunt
              </div>
            ) : null}
          </div>
        )}
        <div className="search-container-wrapper">
          {treasure_hunt ? (
            <span
              className="tooltiptext tooltiptext-search"
              style={{
                marginLeft: '-40px',
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              Search is disabled during the{' '}
              <a style={{ color: '#60fd2d' }}>Christopher Nolan</a> treasure
              hunt
            </span>
          ) : null}
          <div
            className="search-container"
            style={{ opacity: treasure_hunt ? 0.3 : 1.0 }}
          >
            <Search
              gridOverlay={gridOverlay}
              promptOverlayRef={promptOverlayRef}
              sidebarState={sidebarState}
              setMasterTile={setMasterTile}
              setSidebarTitle={setSidebarTitle}
              setSidebar={setSidebar}
              setPopout={setPopout}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setCurrentPrompt={setCurrentPrompt}
              setSingleRegion={setSingleRegion}
              setSubregionMode={setSubregionMode}
              setProjectInfo={setProjectInfo}
              setTilePreview={setTilePreview}
            />
            {treasure_hunt ? (
              <span
                style={{
                  color: 'white',
                  overflowX: 'hidden',
                  opacity: 0.3,
                  fontSize: '13px',
                  paddingTop: '1.5px',
                  paddingLeft: '30px',
                  maxWidth: '260px',
                  width: '100%',
                  fontFamily: "'Andale Mono', AndaleMono, monospace",
                }}
              >
                search keyword or tile #
              </span>
            ) : null}
            <div
              className="search-close"
              onClick={() => {
                closeSearch();
              }}
            >
              {!DetectDevice ? (
                <AiFillCloseCircle className="icon" style={{ fontSize: 14 }} />
              ) : (
                <AiFillCloseCircle className="icon" style={{ fontSize: 18 }} />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="project-title">
        {(projectInfo == 1) & !DetectDevice ? (
          <h1
            onClick={() => {
              setProjectInfo(0);
              map.smoothWheelZoom.enable();
              map.dragging.enable();
            }}
            style={{ marginTop: 185, fontSize: 56, color: 'white' }}
          >
            {' '}
            TODEM{' '}
          </h1>
        ) : (
          <h1
            onClick={() => {
              let background = document.getElementsByClassName(
                'hamburger-background'
              )[0];
              if (background.style.visibility === 'visible') {
                hamburger();
              }
              setProjectInfo(!projectInfo);
              playClose();
            }}
          >
            {' '}
            TODEM{' '}
          </h1>
        )}
      </div>

      {!redactNFTs && !isMobile && !isTablet ? (
        <div>
          <div
            id="connect"
            onClick={(e) => {
              setProjectInfo(1);
              setlistViewExpanded(2);
            }}
            style={{ fontStyle: 'normal', display: 'inline-block', marginRight: blockchain.account ? '182px' : '137px' }} // Assign flex and optional margin
          >
            claim TPX
          </div>
          <div style={{ display: 'inline-block' }}>
            <Wallet
              blockchain={blockchain}
              connectWallet={connectWalletPressed}
              connectCoinbase={connectCoinbasePressed}
              connectWalletConnect={connectWalletConnectPressed}
              disconnectWallet={dicconnectWalletPressed}
            />
          </div>
        </div>
      ) : null}

      {/* Just removed && showVersionToggle */}
      {!projectInfo ? (
        <div className="toggleRenderButton">
          <ToggleButton
            renderNumber={renderNumber}
            sync_flag={sync_flag}
            setRenderNumber={setRenderNumber}
          />
        </div>
      ) : null}
    </div>
  );
};
export default Topbar;
